import { MenuItemSpec } from "@impero-com/common/components/leftSideMenu";
import { t } from "@impero-com/common/utils/i18n";
import {
    PageNavMenu,
    PageNavMenuItem,
    PageNavMenuSection,
} from "@impero-com/ui";

import { AssignmentCount } from "api/generated/model";
import { USER_SUBPATHS } from "org/user/userRoutes";

import { WebRoute } from "../../routes";

/**
 * Every other menu item's id *is* its URL segment (`USER_SUBPATHS.X.path`), so the
 * id and the link it navigates to can't drift apart. The details page is the index
 * route — a menu item with no segment — so it needs a literal of its own.
 */
const ID_USER_DETAILS = "user_details";

function getRiskEntitiesMenuItems(
    assignmentCount: AssignmentCount,
    userId: string
): MenuItemSpec[] {
    return [
        {
            type: "SimpleMenuItem",
            id: USER_SUBPATHS.RISK_ENTITIES.path,
            label: `${t("RiskEntities_Title")} (${assignmentCount.entities.risk})`,
            handler: {
                type: "ROUTER",
                linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.RISK_ENTITIES.path}`,
            },
        },
    ];
}

function getControlEntitiesMenuItems(
    assignmentCount: AssignmentCount,
    userId: string
): MenuItemSpec[] {
    return [
        {
            type: "SimpleMenuItem",
            id: USER_SUBPATHS.CONTROL_ENTITIES.path,
            label: `${t("User_Entities")} (${
                assignmentCount.entities.control
            })`,
            handler: {
                type: "ROUTER",
                linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.CONTROL_ENTITIES.path}`,
            },
        },
    ];
}

function getControlTestingMenuItems(
    assignmentCount: AssignmentCount,
    userId: string
): MenuItemSpec[] {
    const testingControlsCount =
        assignmentCount.testingControlAssignments.direct;
    const testingProgramsCount = assignmentCount.controlTesting.programs;
    const testingActivitiesCount = assignmentCount.controlTesting.activities;
    const totalCount =
        testingControlsCount + testingProgramsCount + testingActivitiesCount;

    return [
        {
            type: "SubMenuItem",
            id: "control_testing",
            label: t("User_ControlTesting", { count: totalCount }),
            childItems: [
                {
                    type: "SimpleMenuItem",
                    id: USER_SUBPATHS.TESTING_CONTROLS.path,
                    label: t("User_ControlTesting_Controls", {
                        count: testingControlsCount,
                    }),
                    handler: {
                        type: "ROUTER",
                        linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.TESTING_CONTROLS.path}`,
                    },
                },
                {
                    type: "SimpleMenuItem",
                    id: USER_SUBPATHS.TESTING_PROGRAMS.path,
                    label: t("User_ControlTesting_Programs", {
                        count: testingProgramsCount,
                    }),
                    handler: {
                        type: "ROUTER",
                        linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.TESTING_PROGRAMS.path}`,
                    },
                },
                {
                    type: "SimpleMenuItem",
                    id: USER_SUBPATHS.TESTING_ACTIVITIES.path,
                    label: t("User_ControlTesting_Activities", {
                        count: testingActivitiesCount,
                    }),
                    handler: {
                        type: "ROUTER",
                        linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.TESTING_ACTIVITIES.path}`,
                    },
                },
            ],
        },
    ];
}

interface MenuItemsOptions {
    assignmentCount: AssignmentCount;
    userId: string;
    controlTestingEnabled: boolean;
    controlEntitiesEnabled: boolean;
    riskEntitiesEnabled: boolean;
}

function createMenuItems({
    assignmentCount,
    userId,
    controlTestingEnabled,
    controlEntitiesEnabled,
    riskEntitiesEnabled,
}: MenuItemsOptions): MenuItemSpec[] {
    return [
        {
            type: "SimpleMenuItem",
            id: ID_USER_DETAILS,
            label: t("User_DetailsTitle"),
            handler: { type: "ROUTER", linkTo: "/users/" + userId },
        },
        {
            type: "SubMenuItem",
            id: "access_and_assignments",
            label: t("User_AccessAndAssignments"),
            childItems: [
                {
                    type: "SimpleMenuItem",
                    id: USER_SUBPATHS.GROUPS.path,
                    label: `${t("User_Groups")} (${assignmentCount.groups})`,
                    handler: {
                        type: "ROUTER",
                        linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.GROUPS.path}`,
                    },
                },
                {
                    type: "SimpleMenuItem",
                    id: USER_SUBPATHS.CONTROLS.path,
                    label: `${t("User_Controls")} (${
                        assignmentCount.controlAssignments.direct
                    })`,
                    handler: {
                        type: "ROUTER",
                        linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.CONTROLS.path}`,
                    },
                },

                {
                    type: "SimpleMenuItem",
                    id: USER_SUBPATHS.PROGRAMS.path,
                    label: `${t("User_ControlPrograms")} (${
                        assignmentCount.controlPrograms
                    })`,
                    handler: {
                        type: "ROUTER",
                        linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.PROGRAMS.path}`,
                    },
                },
                {
                    type: "SubMenuItem",
                    id: "pending_activities",
                    label: `${t("User_PendingActivities")} (${
                        assignmentCount.controlResults.direct +
                        assignmentCount.controlResults.pool
                    })`,
                    childItems: [
                        {
                            type: "SimpleMenuItem",
                            id: USER_SUBPATHS.DIRECT_CONTROL_RESULTS.path,
                            label: `${t(
                                "User_PendingActivities_AssignedDirectly"
                            )} (${assignmentCount.controlResults.direct})`,
                            handler: {
                                type: "ROUTER",
                                linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.DIRECT_CONTROL_RESULTS.path}`,
                            },
                        },
                        {
                            type: "SimpleMenuItem",
                            id: USER_SUBPATHS.GROUP_CONTROL_RESULTS.path,
                            label: `${t(
                                "User_PendingActivities_AssignedViaGroups"
                            )} (${assignmentCount.controlResults.pool})`,
                            handler: {
                                type: "ROUTER",
                                linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.GROUP_CONTROL_RESULTS.path}`,
                            },
                        },
                    ],
                },
                ...(controlTestingEnabled
                    ? getControlTestingMenuItems(assignmentCount, userId)
                    : []),
                {
                    type: "SubMenuItem",
                    id: "reports",
                    label: `${t("Report_MenuTitle")} (${
                        assignmentCount.savedReports +
                        assignmentCount.pushReports.responsible +
                        assignmentCount.pushReports.recipient
                    })`,
                    childItems: [
                        {
                            type: "SimpleMenuItem",
                            id: USER_SUBPATHS.CUSTOM_REPORTS.path,
                            label: `${t("User_CustomReports")} (${
                                assignmentCount.savedReports
                            })`,
                            handler: {
                                type: "ROUTER",
                                linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.CUSTOM_REPORTS.path}`,
                            },
                        },
                        {
                            type: "SimpleMenuItem",
                            id: USER_SUBPATHS.PUSH_REPORTS_RESPONSIBLE.path,
                            label: `${t("User_PushReports_Responsible")} (${
                                assignmentCount.pushReports.responsible
                            })`,
                            handler: {
                                type: "ROUTER",
                                linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.PUSH_REPORTS_RESPONSIBLE.path}`,
                            },
                        },
                        {
                            type: "SimpleMenuItem",
                            id: USER_SUBPATHS.PUSH_REPORTS_RECIPIENT.path,
                            label: `${t("User_PushReports_Recipient")} (${
                                assignmentCount.pushReports.recipient
                            })`,
                            handler: {
                                type: "ROUTER",
                                linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.PUSH_REPORTS_RECIPIENT.path}`,
                            },
                        },
                    ],
                },
                {
                    type: "SubMenuItem",
                    id: "risk_management",
                    label: `${t("RiskManagement_MenuTitle")} (${
                        (riskEntitiesEnabled
                            ? assignmentCount.entities.risk
                            : 0) + assignmentCount.riskDirectories
                    })`,
                    childItems: [
                        ...(riskEntitiesEnabled
                            ? getRiskEntitiesMenuItems(assignmentCount, userId)
                            : []),
                        {
                            type: "SimpleMenuItem",
                            id: USER_SUBPATHS.RISK_DIRECTORIES.path,
                            label: `${t("User_RiskDirectories")} (${
                                assignmentCount.riskDirectories
                            })`,
                            handler: {
                                type: "ROUTER",
                                linkTo: `${WebRoute.USERS}/${userId}/${USER_SUBPATHS.RISK_DIRECTORIES.path}`,
                            },
                        },
                    ],
                },
                ...(controlEntitiesEnabled
                    ? getControlEntitiesMenuItems(assignmentCount, userId)
                    : []),
            ],
        },
    ];
}

interface UserViewMenuOptions {
    assignmentCount: AssignmentCount;
    controlTestingEnabled: boolean;
    controlEntitiesEnabled: boolean;
    riskEntitiesEnabled: boolean;
}

interface UserViewMenuProps extends UserViewMenuOptions {
    userId: string;
    userName: string;
    segment: string;
}

function UserViewMenu({
    userId,
    userName,
    segment,
    ...options
}: UserViewMenuProps) {
    const userPath = `${WebRoute.USERS}/${userId}`;

    return (
        <PageNavMenu aria-label={userName}>
            {userViewMenuSections(options).map(({ label, rows }) => (
                <PageNavMenuSection key={rows[0].segment} label={label}>
                    {rows.map((row) => (
                        <PageNavMenuItem
                            key={row.segment}
                            label={row.label}
                            to={
                                row.segment
                                    ? `${userPath}/${row.segment}`
                                    : userPath
                            }
                            count={row.count === 0 ? undefined : row.count}
                            active={row.segment === segment}
                        />
                    ))}
                </PageNavMenuSection>
            ))}
        </PageNavMenu>
    );
}

interface UserViewMenuSection {
    label?: string;
    rows: Array<UserViewMenuRow>;
}

interface UserViewMenuRow {
    /** The URL segment under `/users/:id`, empty for User details. */
    segment: string;
    label: string;
    count?: number;
}

function userViewMenuSections({
    assignmentCount,
    controlTestingEnabled,
    controlEntitiesEnabled,
    riskEntitiesEnabled,
}: UserViewMenuOptions): Array<UserViewMenuSection> {
    const controlTesting: Array<UserViewMenuSection> = controlTestingEnabled
        ? [
              {
                  label: t("ControlTesting_ModuleTitle"),
                  rows: [
                      {
                          segment: USER_SUBPATHS.TESTING_CONTROLS.path,
                          label: t("User_TestingControls"),
                          count: assignmentCount.testingControlAssignments
                              .direct,
                      },
                      {
                          segment: USER_SUBPATHS.TESTING_PROGRAMS.path,
                          label: t("User_TestingPrograms"),
                          count: assignmentCount.controlTesting.programs,
                      },
                      {
                          segment: USER_SUBPATHS.TESTING_ACTIVITIES.path,
                          label: t("User_TestingActivities"),
                          count: assignmentCount.controlTesting.activities,
                      },
                  ],
              },
          ]
        : [];
    const controlEntities: Array<UserViewMenuSection> = controlEntitiesEnabled
        ? [
              {
                  rows: [
                      {
                          segment: USER_SUBPATHS.CONTROL_ENTITIES.path,
                          label: t("User_Entities"),
                          count: assignmentCount.entities.control,
                      },
                  ],
              },
          ]
        : [];
    const riskEntities: Array<UserViewMenuRow> = riskEntitiesEnabled
        ? [
              {
                  segment: USER_SUBPATHS.RISK_ENTITIES.path,
                  label: t("RiskEntities_Title"),
                  count: assignmentCount.entities.risk,
              },
          ]
        : [];

    return [
        { rows: [{ segment: "", label: t("User_DetailsTitle") }] },
        {
            label: t("User_AccessAndAssignments"),
            rows: [
                {
                    segment: USER_SUBPATHS.GROUPS.path,
                    label: t("User_Groups"),
                    count: assignmentCount.groups,
                },
                {
                    segment: USER_SUBPATHS.CONTROLS.path,
                    label: t("User_Controls"),
                    count: assignmentCount.controlAssignments.direct,
                },
                {
                    segment: USER_SUBPATHS.PROGRAMS.path,
                    label: t("User_ControlPrograms"),
                    count: assignmentCount.controlPrograms,
                },
            ],
        },
        {
            label: t("User_PendingActivities"),
            rows: [
                {
                    segment: USER_SUBPATHS.DIRECT_CONTROL_RESULTS.path,
                    label: t("User_PendingActivities_AssignedDirectly"),
                    count: assignmentCount.controlResults.direct,
                },
                {
                    segment: USER_SUBPATHS.GROUP_CONTROL_RESULTS.path,
                    label: t("User_PendingActivities_AssignedViaGroups"),
                    count: assignmentCount.controlResults.pool,
                },
            ],
        },
        ...controlTesting,
        {
            label: t("Report_MenuTitle"),
            rows: [
                {
                    segment: USER_SUBPATHS.CUSTOM_REPORTS.path,
                    label: t("User_CustomReports"),
                    count: assignmentCount.savedReports,
                },
                {
                    segment: USER_SUBPATHS.PUSH_REPORTS_RESPONSIBLE.path,
                    label: t("User_PushReports_Responsible"),
                    count: assignmentCount.pushReports.responsible,
                },
                {
                    segment: USER_SUBPATHS.PUSH_REPORTS_RECIPIENT.path,
                    label: t("User_PushReports_Recipient"),
                    count: assignmentCount.pushReports.recipient,
                },
            ],
        },
        {
            label: t("RiskManagement_MenuTitle"),
            rows: [
                {
                    segment: USER_SUBPATHS.RISK_DIRECTORIES.path,
                    label: t("User_RiskDirectories"),
                    count: assignmentCount.riskDirectories,
                },
                ...riskEntities,
            ],
        },
        ...controlEntities,
    ];
}

export { createMenuItems, ID_USER_DETAILS, UserViewMenu };
